import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { SearchableSelectOption } from './SearchableSelect';

interface MultiSelectFilterProps {
  ariaLabel: string;
  values: readonly string[];
  onChange: (values: string[]) => void;
  options: readonly SearchableSelectOption[];
  placeholder: string;
  disabled?: boolean;
  className?: string;
  // For option lists too large to browse unfiltered (e.g. every item ID in
  // the fleet): only render matches once the operator has typed a query,
  // rather than dumping every option in the DOM on open. Already-selected
  // options stay visible so a paste (see below) can still be reviewed.
  requireSearchToBrowse?: boolean;
}

// Splits pasted text like "WS-EVT2-005, WS-EVT2-004\nWS-EVT2-003" into
// individual tokens so a list of known values (e.g. serial numbers copied
// from a shipping manifest) can be pasted in and matched in one action,
// instead of searching and checking each one by hand.
function splitPastedTokens(text: string): string[] {
  return text
    .split(/[\s,]+/)
    .map((token) => token.trim())
    .filter(Boolean);
}

function searchText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase();
}

export function MultiSelectFilter({
  ariaLabel,
  values,
  onChange,
  options,
  placeholder,
  disabled = false,
  className,
  requireSearchToBrowse = false,
}: MultiSelectFilterProps) {
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const selectAllRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [draftValues, setDraftValues] = useState<Set<string>>(() => new Set(values));
  const selectedOptions = options.filter((option) => values.includes(option.value));
  const selectionCoversAllOptions =
    options.length > 0 && options.every((option) => values.includes(option.value));
  const selectedLabel =
    selectedOptions.length === 0 || selectionCoversAllOptions
      ? placeholder
      : selectedOptions.map((option) => option.label).join(', ');
  const visibleOptions = useMemo(() => {
    const normalizedQuery = searchText(query.trim());
    if (!normalizedQuery) {
      if (!requireSearchToBrowse) return options;
      // Nothing typed yet: show only what's already checked, so a paste can
      // be reviewed without rendering the entire (potentially huge) list.
      return options.filter((option) => draftValues.has(option.value));
    }
    const matches = options.filter((option) =>
      [option.label, option.value, ...(option.aliases ?? [])]
        .map(searchText)
        .some((candidate) => candidate.includes(normalizedQuery)),
    );
    // Cap the rendered list so a broad query (e.g. "G8") against a
    // fleet-sized option set doesn't flood the DOM. Checked options are
    // kept so a review/paste still shows everything selected.
    if (matches.length <= 100) return matches;
    const selected = matches.filter((option) => draftValues.has(option.value));
    const rest = matches.filter((option) => !draftValues.has(option.value)).slice(0, 100);
    return [...selected, ...rest];
  }, [draftValues, options, query, requireSearchToBrowse]);
  const optionByToken = useMemo(() => {
    const map = new Map<string, string>();
    for (const option of options) {
      for (const token of [option.value, option.label, ...(option.aliases ?? [])]) {
        map.set(searchText(token), option.value);
      }
    }
    return map;
  }, [options]);
  const allSelected =
    options.length > 0 && options.every((option) => draftValues.has(option.value));
  const someSelected = options.some((option) => draftValues.has(option.value));

  useEffect(() => {
    if (!open) setDraftValues(new Set(values));
  }, [open, values]);

  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate = someSelected && !allSelected;
  }, [allSelected, someSelected]);

  useEffect(() => {
    function closeOnOutsidePointer(event: MouseEvent): void {
      if (rootRef.current?.contains(event.target as Node)) return;
      setOpen(false);
      setQuery('');
    }
    document.addEventListener('mousedown', closeOnOutsidePointer);
    return () => document.removeEventListener('mousedown', closeOnOutsidePointer);
  }, []);

  function openPicker(): void {
    if (disabled) return;
    setDraftValues(new Set(values));
    setQuery('');
    setOpen(true);
    requestAnimationFrame(() => searchRef.current?.focus());
  }

  function apply(): void {
    // Selecting every value is semantically the same as no filter. Normalize
    // it to the default state so the trigger, URL, and active-filter chips
    // stay honest rather than reporting an artificial "N selected" state.
    onChange(
      allSelected
        ? []
        : options.filter((option) => draftValues.has(option.value)).map((option) => option.value),
    );
    setOpen(false);
    setQuery('');
  }

  function toggle(value: string): void {
    setDraftValues((current) => {
      const next = new Set(current);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }

  return (
    <div
      className={`multi-select-filter${open ? ' multi-select-filter-open' : ''}${className ? ` ${className}` : ''}`}
      ref={rootRef}
    >
      <button
        aria-controls={menuId}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`${ariaLabel}: ${selectedLabel}`}
        className="multi-select-filter-trigger"
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openPicker())}
        type="button"
      >
        <span>{selectedLabel}</span>
        <span className="multi-select-filter-indicator" aria-hidden="true" />
      </button>
      {open ? (
        <div
          className="multi-select-filter-menu"
          id={menuId}
          role="dialog"
          aria-label={`Filter ${ariaLabel}`}
        >
          <div className="multi-select-filter-heading">Select values</div>
          <input
            aria-label={`Search ${ariaLabel}`}
            className="multi-select-filter-search"
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                setOpen(false);
                setQuery('');
              }
              if (event.key === 'Enter') {
                event.preventDefault();
                apply();
              }
            }}
            onPaste={(event) => {
              const tokens = splitPastedTokens(event.clipboardData.getData('text'));
              if (tokens.length < 2) return; // a single pasted value just goes into the search box
              event.preventDefault();
              const matches = tokens
                .map((token) => optionByToken.get(searchText(token)))
                .filter((value): value is string => value !== undefined);
              if (matches.length > 0) {
                setDraftValues((current) => new Set([...current, ...matches]));
              }
              setQuery('');
            }}
            placeholder={`Filter ${ariaLabel.toLocaleLowerCase()}`}
            ref={searchRef}
            value={query}
          />
          {requireSearchToBrowse ? null : (
            <label className="multi-select-filter-select-all">
              <input
                checked={allSelected}
                onChange={() =>
                  setDraftValues(
                    allSelected ? new Set() : new Set(options.map((option) => option.value)),
                  )
                }
                ref={selectAllRef}
                type="checkbox"
              />
              <span>Select all</span>
            </label>
          )}
          <div className="multi-select-filter-options" role="listbox" aria-multiselectable="true">
            {visibleOptions.length > 0 ? (
              visibleOptions.map((option) => {
                const checked = draftValues.has(option.value);
                return (
                  <label className="multi-select-filter-option" key={option.value}>
                    <input
                      checked={checked}
                      onChange={() => toggle(option.value)}
                      type="checkbox"
                    />
                    <span>{option.label}</span>
                  </label>
                );
              })
            ) : requireSearchToBrowse && !query.trim() ? (
              <p className="searchable-select-empty">Search or paste values.</p>
            ) : (
              <p className="searchable-select-empty">No matches</p>
            )}
          </div>
          <div className="multi-select-filter-actions">
            <button className="primary" onClick={apply} type="button">
              Apply
            </button>
            <button
              onClick={() => {
                setDraftValues(new Set(values));
                setOpen(false);
                setQuery('');
              }}
              type="button"
            >
              Cancel
            </button>
            <button className="button-link" onClick={() => setDraftValues(new Set())} type="button">
              Clear
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
